/**
 * THE OVERVIEW — the cabinet's landing surface.
 *
 * It answers three questions, in reads that each stand alone - one failing blanks no other:
 *   getDashboard      the chosen period's tiles, and the period before it for each change (below)
 *   getPaymentsView   for the running mid-month figure, which lives on that response and nowhere
 *                     else (decision-log row 138: an addition to getPaymentsView, not a new one)
 *   list totals       what is waiting on the author - WHAT TO DO TODAY, below
 *
 * EVERY TILE CARRIES A PER-TILE STATE AND NEVER A BARE ZERO. `ok`, `no_data` and
 * `not_reported_yet` are three different facts and the third is the common one at launch: four
 * tiles have no shipped client sending their event. See ../../features/cabinet/components/metric.
 *
 * FIVE TILES HERE, THE WHOLE SET ON STATISTICS (design 15). Views, new followers, likes, comments,
 * as the prototype draws them, and all-time views beside the period's (below). Counted views, the
 * readers, tips, messages and the rest are on Statistics (../articles/ArticleStatsRoute.tsx), which
 * reads the same operation over the same days.
 *
 * THE RANGE IS NEVER THE BROWSER'S. The first read omits `from`/`to`, so the SERVER picks them as
 * UTC calendar days on its own clock, and this app has no clock at all (C11 plus D9 - see
 * ../../features/cabinet/format.ts). Every set period is counted back from the `to` that RESPONSE
 * reports, a custom range ends no later than it, and the button shows the days it asks for.
 *
 * ROUTE. `/` is the natural home for this and the corpus fixes only `/enter`; layout, wording and
 * navigation are ours under decision-log rows 99/100/108. The descriptor in
 * ../../features/cabinet/routes.ts names `/dashboard` so it can be mounted beside a shell this
 * track does not own; moving it to `/` is a one-line change there.
 *
 * WHAT TO DO TODAY (./NeedsAttention.tsx). Four counted rows, each a platform `total` - articles
 * that need changes, comments and messages waiting for a reply, drafts - read through the same
 * functions as the sidebar's badges, and each row drawn only above 0. The prototype's fourth row,
 * "Your July statement is ready to download", is LEFT OUT: no operation says a statement is new to
 * its author, so it would be a permanent line rather than attention. That file has the table and
 * the reasons.
 *
 * ★ ALL-TIME VIEWS, A TILE OF ITS OWN (the owner, 2026-10-05: "for the author it is one app, one
 * data - no separate 'earlier views'"). Views are named by time, never by where they happened:
 * "Views" stays the chosen period's, with its change, and "All-time views" beside it is every view
 * ever, from both apps, in one number - `allTimeViews`, read by `allTimeViewsOf`
 * (../../features/cabinet/all-time-views.ts), which takes `oldAppViews` from a server older than
 * the member and leaves the tile out when neither is there. It has no change: all time has no
 * period before it. Shown, never paid, as the old apps' views always were (row 231). The sentence
 * design 14 put under the tiles - "Your articles also have 1,234 earlier views. Earlier views are
 * not paid." - is gone: those views are inside the all-time figure, and nothing here says
 * "earlier" about views. With the tile the row is five (`TileRow columns={5}`), else four.
 *
 * ★ THE SESSION, IN THE APP (design 14, F11). The router mounts this screen with NO props
 * (`gated` in src/router.tsx renders `<Screen />`), so `session` was always undefined in the app,
 * and the two notes that read it - the on-hold banner, and a "There are new terms to read" banner
 * - were never drawn there; the walk met an author with terms waiting and an Overview that never
 * said so. A live build now takes the session `AuthSessionProvider` holds (`LiveOverview`), so
 * nothing is read twice; the default build - fixtures, nobody signed in - takes the prop, as before,
 * and the branch is decided per build, so the hook order never changes (the shape
 * `routes/support/session-routes.tsx` uses). The terms banner is GONE: owed terms are now the first
 * row of "What to do today", with the link to where they are accepted, and one screen saying
 * the same thing twice would be one too many. Its money sentence went with it, to the row's line.
 *
 * ★ THE PROTOTYPE'S OVERVIEW (design 15 - the owner's live look at real cabinets, 2026-10-01:
 * "adapt UI/styles/layouts to the prototype", and "agree on all"). Top to bottom, as the prototype
 * draws it: the header, "Where things stand right now, and what is waiting on you."; the period
 * button with its days (design 24, T15) and a quiet line saying what is compared; the tiles,
 * each with its change (All-time views has none); "What to do today"; then the month's estimate
 * (./MidMonthEarnings.tsx). Every internal phrase this screen used to say - "the platform's own
 * reporting window", "UTC days, chosen by the platform", "per UTC day", "recomputed downward" - is
 * gone: an author is a user (the owner's rule, design 15 section 2).
 *
 * ★ THE PERIOD AND THE CHANGES (design 15, decision 2; design 24, T15: one button, as Statistics',
 * its set periods and a custom range), Last 30 days chosen first. Three reads of one operation: the
 * default read, for its `to` alone (the server's latest complete day); the chosen period, `rangeOf`;
 * and as many days just before it, `previousPeriod` - both in ../../features/cabinet/ranges.ts,
 * which reads no clock. A tile's change is `changeBetween(now, before)`: none when either figure is
 * unknown or the period before had nothing, since a rise from nothing has no percentage. A
 * `no_data` tile is a true 0, on screen and in the change; a `not_reported_yet` one is the quiet
 * "Not measured yet" and is never compared. While a newly chosen period is read, the last period's
 * figures stay under "Refreshing…" with no change drawn - never shown as the new period's.
 *
 * ★ A PERIOD WITH NOTHING COUNTED IS ONE QUIET LINE (the owner's list, item 5, 2026-10-05). It was a
 * boxed "Nothing to report for these days yet" above the row of zeros: two answers to one question.
 * Now one dimmed sentence stands where the box was - "No views, likes or comments counted for these
 * days yet. If you have just published, give it a day: each day is counted once it is over." - and
 * the tiles stay as they are, since their zeros are true. When it is drawn is unchanged. It names
 * what was not counted, as the box did, rather than saying "nothing": Statistics draws it when only
 * followers moved, beside a Followers tile that says so. It is plain text, read in order with the
 * tiles beside it; neither is announced on a period change, only "Refreshing…" is. Statistics'
 * Overview tab says the same sentence, word for word (../articles/StatsOverview.tsx).
 */
import { Alert, Group, Skeleton, Stack, Text } from '@mantine/core';
import { TipsConsentNotice } from './TipsConsentNotice';
import { useCallback, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { PeriodPicker } from '@moonx/ui';
import {
  CabinetPageHeader,
  ErrorState,
  LoadingState,
  PartialFailureNote,
  RefreshingNote,
  StatTile,
  TileRow,
  tileValue,
} from '../../features/cabinet/components';
import { useAsync } from '../../features/cabinet/state';
import type { AsyncResult } from '../../features/cabinet/state';
import { useCabinetApi } from '../../features/cabinet/api';
import { allTimeViewsOf } from '../../features/cabinet/all-time-views';
import { formatCount, formatDayRange, formatPeriod } from '../../features/cabinet/format';
import {
  PERIOD_LABELS,
  changeBetween,
  daysIn,
  isDay,
  periodButtonFor,
  previousPeriod,
  rangeOf,
} from '../../features/cabinet/ranges';
import type { DayRange, PeriodChoice } from '../../features/cabinet/ranges';
import { useAuthSession } from '../../features/auth/session/AuthSessionProvider';
import { useCurrentTerms } from '../../features/support';
import { termsAcceptanceOwed } from '../../features/shell/cabinet-attention';
import { isLiveApiConfigured } from '../../config';
import { useTranslation } from '../../i18n';
import type { TFunction } from 'i18next';
import { MidMonthEarnings } from './MidMonthEarnings';
import { NeedsAttention } from './NeedsAttention';
import classes from './overview.module.css';
import type { AuthorSession, CabinetApi, Dashboard, MetricTile } from '../../features/cabinet/api';

export interface DashboardRouteProps {
  /**
   * The session, when the caller has one to hand in. Optional: the router mounts this screen with
   * no props, and a live build then reads the session the provider holds (see the header). With
   * neither, it degrades to no on-hold banner and no terms row rather than to not rendering.
   */
  session?: AuthorSession;
}

export function DashboardRoute({ session }: DashboardRouteProps) {
  return isLiveApiConfigured() ? (
    <LiveOverview session={session} />
  ) : (
    <Overview session={session} />
  );
}

/** What the screen draws from: a session, or none. */
interface OverviewProps {
  readonly session: AuthorSession | undefined;
  /** `getCurrentTerms`'s `accepted`, read only by a live build; undefined while unread. */
  readonly termsAccepted?: boolean | undefined;
}

/** A live build: the session `AuthSessionProvider` already holds - never a second `GET /v1/me`. */
function LiveOverview({ session }: OverviewProps) {
  const { state } = useAuthSession();
  const held = session ?? state.session ?? undefined;
  // The session answers for the agreements since design 25, and its answer is all the Overview
  // reads. A session silent on them has the author agreement read instead (cabinet-attention.ts,
  // `termsAcceptanceOwed`), as Support does - in a component of its own, so each keeps its hooks.
  return held !== undefined && held.termsAcceptanceRequired === undefined ? (
    <OverviewReadingTerms session={held} />
  ) : (
    <Overview session={held} />
  );
}

/** The Overview for a session silent on the agreements: `getCurrentTerms`, read once. */
function OverviewReadingTerms({ session }: OverviewProps) {
  const terms = useCurrentTerms();
  const termsAccepted =
    terms.status === 'ready' && terms.data !== null ? terms.data.accepted : undefined;
  return <Overview session={session} termsAccepted={termsAccepted} />;
}

function Overview({ session, termsAccepted }: OverviewProps) {
  const { t } = useTranslation('dashboard');
  const api = useCabinetApi();

  // The default range: read once, for the day the SERVER calls its latest complete one (`to`).
  const readLatest = useCallback(
    (signal: AbortSignal) => api.getDashboard(undefined, signal),
    [api],
  );
  const readPayments = useCallback((signal: AbortSignal) => api.getPaymentsView(signal), [api]);

  const latest = useAsync(readLatest, 'dashboard:default-range');
  const payments = useAsync(readPayments, 'payments:view');

  return (
    <Stack gap="xl">
      <CabinetPageHeader title={t('overview.title')} lede={t('overview.lede')} />

      {session?.authorStatus === 'disabled' ? (
        <Alert
          variant="light"
          role="status"
          title={t('overview.onHold.title')}
          styles={{
            root: {
              backgroundColor: 'oklch(var(--mx-warning) / 0.10)',
              border: '1px solid oklch(var(--mx-warning) / 0.45)',
            },
            // The warning INK: light `warning` read 3.99 on this tint over the page, the ink 6.04;
            // dark is `warning` itself (web/authors/test/tinted-inks.spec.tsx).
            title: { color: 'oklch(var(--mx-warning-ink))' },
          }}
        >
          {/* cabinetReachable stays true for a disabled author: they reach the cabinet, see the
              reason, and only payout actions are refused. The cabinet is never a locked door. */}
          <Text size="sm">{session.disabledReason ?? t('overview.onHold.body')}</Text>
        </Alert>
      ) : null}

      <TipsConsentNotice />

      {/* ---- the period and its tiles ---------------------------------------------------- */}
      {latest.status === 'error' && latest.data === undefined ? (
        <ErrorState
          title={t('overview.figuresFailed')}
          error={latest.error}
          onRetry={latest.refetch}
        />
      ) : latest.data === undefined ? (
        <FiguresLoading />
      ) : isDay(latest.data.to) ? (
        <PeriodFigures latestDay={latest.data.to} />
      ) : (
        // A `to` this app cannot count back from: no period button, and the default range's figures.
        <FiguresSection>
          <Text size="xs" fw={700} className={classes.range}>
            {formatDayRange(latest.data.from, latest.data.to)}
          </Text>
          <Figures now={latest.data} before={undefined} />
        </FiguresSection>
      )}

      {/* ---- what is waiting on the author: its own reads, so a failure stays here -------- */}
      {/* Owed creator terms are its FIRST row - not a banner of their own (see the header). */}
      <AttentionFor
        session={session}
        termsAccepted={termsAccepted}
        today={payments.data?.computedAt}
      />

      {/* ---- the month's estimate ------------------------------------------------------- */}
      {payments.status === 'loading' && payments.data === undefined ? (
        <LoadingState label={t('overview.readingMonth')} />
      ) : payments.status === 'error' ? (
        // A partial state: the tiles above can still be right when this one is not.
        <PartialFailureNote what={t('overview.monthFigure')} error={payments.error} />
      ) : payments.data === undefined ? null : (
        <MidMonthEarnings payments={payments.data} />
      )}
    </Stack>
  );
}

/* ------------------------------------------------------------------------------ the period */

/** The period chosen when the Overview opens - the prototype's. */
const DEFAULT_PERIOD: PeriodChoice = { kind: 'last30' };

/** One read of a period: the days asked for, and the answer. */
interface PeriodReading {
  readonly range: DayRange;
  readonly dashboard: Dashboard;
}

function sameDays(a: DayRange, b: DayRange): boolean {
  return a.from === b.from && a.to === b.to;
}

/** `getDashboard` over exactly `range`; a new range aborts the read of the last one. */
function usePeriodReading(api: CabinetApi, range: DayRange): AsyncResult<PeriodReading> {
  const read = useCallback(
    async (signal: AbortSignal): Promise<PeriodReading> => ({
      range,
      dashboard: await api.getDashboard({ from: range.from, to: range.to }, signal),
    }),
    [api, range],
  );
  return useAsync(read, `dashboard:${range.from}..${range.to}`);
}

/** The quiet line under the button: what the period is, and what each change compares it with. */
function periodNote(choice: PeriodChoice, range: DayRange, t: TFunction<'dashboard'>): string {
  const days = daysIn(range);
  if (choice.kind === 'thisMonth') {
    const before = days === 1 ? t('period.dayBefore') : t('period.daysBefore', { count: days });
    return t('period.thisMonth', { month: formatPeriod(range.to.slice(0, 7)), before });
  }
  const name = choice.kind === 'custom' ? t('period.custom') : PERIOD_LABELS[choice.kind];
  const before = days === 1 ? t('period.dayBefore') : t('period.previousDays', { count: days });
  return t('period.compared', { name, before });
}

/** The period button and the period's tiles, counted back from the server's `latestDay`. */
function PeriodFigures({ latestDay }: { readonly latestDay: string }) {
  const { t } = useTranslation('dashboard');
  const api = useCabinetApi();
  const [choice, setChoice] = useState<PeriodChoice>(DEFAULT_PERIOD);
  const range = useMemo(() => rangeOf(choice, latestDay), [latestDay, choice]);
  const before = useMemo(() => previousPeriod(range), [range]);

  const current = usePeriodReading(api, range);
  const previous = usePeriodReading(api, before);

  // `useAsync` keeps the last answer while a new key is read: it may be the LAST period's.
  const shown = current.data;
  const fresh = shown !== undefined && sameDays(shown.range, range);
  const comparable =
    fresh && previous.data !== undefined && sameDays(previous.data.range, before)
      ? previous.data.dashboard
      : undefined;
  const reading = !fresh && shown !== undefined && current.status !== 'error';

  return (
    <FiguresSection>
      <Group justify="space-between" align="center" gap="md" wrap="wrap">
        {/* One button for the period, Statistics' own (design 24, T15). */}
        <PeriodPicker
          label={t('period.label')}
          {...periodButtonFor(choice, latestDay, setChoice)}
        />
      </Group>
      <Group gap="sm" wrap="wrap">
        <Text className={classes.periodNote}>{periodNote(choice, range, t)}</Text>
        {reading || (fresh && comparable === undefined && previous.status !== 'error') ? (
          <RefreshingNote label={t('overview.refreshing')} />
        ) : null}
      </Group>

      {current.status === 'error' && !fresh ? (
        <ErrorState
          title={t('overview.figuresFailed')}
          error={current.error}
          onRetry={current.refetch}
        />
      ) : shown === undefined ? (
        <TileSkeletons />
      ) : (
        <>
          {fresh && previous.status === 'error' ? (
            // The tiles stand without their changes; one note says why there are none.
            <PartialFailureNote what={t('overview.compareFigures')} error={previous.error} />
          ) : null}
          <Figures now={shown.dashboard} before={comparable} />
        </>
      )}
    </FiguresSection>
  );
}

/** The figures' region: named for a screen reader's outline, though the prototype draws no title. */
function FiguresSection({ children }: { readonly children: ReactNode }) {
  const { t } = useTranslation('dashboard');
  const headingId = useId();
  return (
    <Stack component="section" gap="sm" aria-labelledby={headingId}>
      <h2 id={headingId} className={classes.visuallyHidden}>
        {t('overview.figuresHeading')}
      </h2>
      {children}
    </Stack>
  );
}

function FiguresLoading() {
  const { t } = useTranslation('dashboard');
  return (
    <Stack gap="sm">
      <LoadingState label={t('overview.readingNumbers')} />
      <TileSkeletons />
    </Stack>
  );
}

function TileSkeletons() {
  return (
    <TileRow columns={5}>
      <Skeleton height={124} radius="xl" />
      <Skeleton height={124} radius="xl" />
      <Skeleton height={124} radius="xl" />
      <Skeleton height={124} radius="xl" />
      <Skeleton height={124} radius="xl" />
    </TileRow>
  );
}

/* ------------------------------------------------------------------------------- the tiles */

/**
 * A tile's figure, for the screen and for a change: `no_data` is a TRUE 0 (the schema: "the range
 * really is empty and zero is true"); `not_reported_yet` and an `ok` with no value are no figure.
 */
function figureOf(tile: MetricTile): number | undefined {
  return tile.state === 'no_data' ? 0 : tileValue(tile);
}

/**
 * The followers tile's `gainedInRange` as a tile of its own. The server leaves it out of an `ok`
 * tile when no day in the range had a row - the `no_data` of every other tile, so a true 0 here too
 * (src/modules/analytics/cabinet/dashboard.presenter.ts, `followersTile`).
 */
function newFollowersOf(dashboard: Dashboard): MetricTile {
  const followers = dashboard.tiles.followers;
  return followers.state === 'ok'
    ? { state: 'ok', value: followers.gainedInRange ?? 0 }
    : { state: followers.state };
}

/**
 * All-time views as a tile: every view ever, one number (see the header) - or none, when the answer
 * has no such figure, and the tile is left out rather than drawn with an invented one.
 */
function allTimeViewsTileOf(dashboard: Dashboard): MetricTile | undefined {
  const views = allTimeViewsOf(dashboard);
  return views === undefined ? undefined : { state: 'ok', value: views };
}

/**
 * The Overview's tiles: the prototype's four, in its order and with its words where it has them,
 * and All-time views beside the period's Views (the owner, 2026-10-05).
 */
const OVERVIEW_TILES: ReadonlyArray<{
  /** Its label and tip in the catalogue, `tiles.<key>`. */
  readonly key: 'views' | 'allTimeViews' | 'newFollowers' | 'likes' | 'comments';
  /** The tile for an answer; `undefined` leaves it out. */
  readonly tile: (dashboard: Dashboard) => MetricTile | undefined;
  /** False for a figure of all time: there is no period before it to be compared with. */
  readonly compared: boolean;
}> = [
  {
    key: 'views',
    tile: (dashboard) => dashboard.tiles.views,
    compared: true,
  },
  {
    key: 'allTimeViews',
    tile: allTimeViewsTileOf,
    compared: false,
  },
  {
    key: 'newFollowers',
    tile: newFollowersOf,
    compared: true,
  },
  {
    key: 'likes',
    tile: (dashboard) => dashboard.tiles.likes,
    compared: true,
  },
  {
    key: 'comments',
    tile: (dashboard) => dashboard.tiles.comments,
    compared: true,
  },
];

function Figures({
  now,
  before,
}: {
  readonly now: Dashboard;
  readonly before: Dashboard | undefined;
}) {
  const { t } = useTranslation('dashboard');
  const tiles = now.tiles;
  // The tiles this answer has: All-time views only when it carries the figure (see the header).
  const shown = OVERVIEW_TILES.flatMap((spec) => {
    const tile = spec.tile(now);
    return tile === undefined ? [] : [{ spec, tile }];
  });

  // Nothing counted in these days at all. The tiles alone would be a row of honest zeroes and no
  // idea whether something is broken or there are simply no readers yet - so one quiet line above
  // them says which, where a box used to stand (see the header).
  const everythingEmpty =
    tiles.views.state !== 'ok' &&
    tiles.countedViews.state !== 'ok' &&
    tiles.likes.state !== 'ok' &&
    tiles.comments.state !== 'ok' &&
    tiles.tips.state !== 'ok' &&
    (figureOf(newFollowersOf(now)) ?? 0) === 0;

  return (
    <Stack gap="sm">
      {everythingEmpty ? (
        <Text size="sm" c="dimmed">
          {t('overview.nothingCounted')}
        </Text>
      ) : null}

      <TileRow columns={shown.length === 5 ? 5 : 4}>
        {shown.map(({ spec, tile }) => {
          const figure = figureOf(tile);
          // The same tile in the period before - never for a figure of all time.
          const previous = spec.compared && before !== undefined ? spec.tile(before) : undefined;
          return (
            <StatTile
              key={spec.key}
              label={t(`tiles.${spec.key}.label`)}
              value={
                figure !== undefined
                  ? formatCount(figure)
                  : // A figure nobody has - not reported yet, or an `ok` sent without its number:
                    // one wording for both (design 23, F7).
                    t('overview.notMeasured')
              }
              quiet={figure === undefined}
              delta={previous === undefined ? undefined : changeBetween(figure, figureOf(previous))}
              tip={t(`tiles.${spec.key}.tip`)}
            />
          );
        })}
      </TileRow>
    </Stack>
  );
}

/**
 * "What to do today" for this author: the terms when they are owed (design 14, F11), and - since
 * the owner's live walk of real cabinets (2026-10-01) - "Write your first article" for an author
 * with none, never for a disabled one (./NeedsAttention.tsx says why). Since design 25: Payment
 * details while they are not complete, never for a disabled author; and for an applicant, one
 * "Getting started" row instead, with what is left of the profile (the application's own read,
 * there) and the Payment details. `today` is the payments view's `computedAt`: the server's day.
 */
function AttentionFor({
  session,
  termsAccepted,
  today,
}: OverviewProps & { readonly today: string | undefined }) {
  const applying = session?.authorStatus === 'applicant';
  return (
    <NeedsAttention
      termsOwed={termsAcceptanceOwed(session, termsAccepted)}
      invitesFirstArticle={session?.authorStatus !== 'disabled'}
      asksForPaymentDetails={session?.authorStatus !== 'disabled'}
      gettingStarted={applying}
      today={today}
    />
  );
}
